import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  BellAlertIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ClipboardDocumentCheckIcon,
  ExclamationTriangleIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import vehicleMaintenanceApi from '../../api/vehicleMaintenanceApi';
import { sortRowsByVehicleCode, sortVehiclesByVehicleCode } from './internalAuditVehicleCodeSort';
import {
  StatusBadge,
  VehicleModuleHero,
  VehiclePageShell,
  VehiclePlateCell,
  vehiclePlateColumnProps,
  VehicleSelectionPreview,
  VehicleStatCard,
  VehicleStatGrid,
  getVehicleById,
} from './vehicleMaintenanceUi';
import ActionToolbar from '../../components/ui/ActionToolbar';
import DataTable from '../../components/ui/DataTable';
import FilterActions from '../../components/ui/FilterActions';
import FilterPanel from '../../components/ui/FilterPanel';
import IconActionButton from '../../components/ui/IconActionButton';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import ResponsiveModal from '../../components/ui/ResponsiveModal';
import { getVehicleIdFromSearch } from './vehicleQueryParams';

const initialForm = {
  vehicle: '',
  tuvturk_date: '',
  exhaust_date: '',
  tuvturk_done_1: false,
  tuvturk_done_2: false,
  tuvturk_done_3: false,
  tuvturk_done_4: false,
  exhaust_done_1: false,
  exhaust_done_2: false,
  exhaust_done_3: false,
  exhaust_done_4: false,
  notes: '',
};

/** Match backend dateutil +2 calendar years */
function addCalendarYears(isoDate, years) {
  if (!isoDate || years == null) return '';
  const d = new Date(`${isoDate}T12:00:00`);
  d.setFullYear(d.getFullYear() + years);
  return d.toISOString().slice(0, 10);
}

function computeRounds(tuvturkDate, exhaustDate) {
  const rounds = [];
  for (let r = 1; r <= 4; r += 1) {
    const td =
      r === 1
        ? tuvturkDate
        : tuvturkDate
          ? addCalendarYears(tuvturkDate, 2 * (r - 1))
          : '';
    const ed =
      r === 1
        ? exhaustDate
        : exhaustDate
          ? addCalendarYears(exhaustDate, 2 * (r - 1))
          : '';
    const sonMuayene = td ? addCalendarYears(td, 2) : '';
    rounds.push({ tuvturk: td, exhaust: ed, sonMuayene });
  }
  return rounds;
}

function roundTuvIso(tuvturkDate, roundOneBased) {
  if (!tuvturkDate) return '';
  if (roundOneBased === 1) return tuvturkDate;
  return addCalendarYears(tuvturkDate, 2 * (roundOneBased - 1));
}

function roundExhaustIso(exhaustDate, tuvturkDate, roundOneBased) {
  if (!tuvturkDate) return '';
  if (!exhaustDate) return roundTuvIso(tuvturkDate, roundOneBased);
  if (roundOneBased === 1) return exhaustDate;
  return addCalendarYears(exhaustDate, 2 * (roundOneBased - 1));
}

/** İlk tamamlanmamış turun muayene tarihi (backend inspection_active_due_date ile uyumlu). */
function computeActiveInspectionDueIso(row) {
  const t = row?.tuvturk_date;
  if (!t) return '';
  const e = row?.exhaust_date;
  const exhaustApplicable = !!e;
  for (let r = 1; r <= 4; r += 1) {
    const td = roundTuvIso(t, r);
    const ed = exhaustApplicable ? roundExhaustIso(e, t, r) : '';
    const tDone = !!row?.[`tuvturk_done_${r}`];
    const eDone = exhaustApplicable ? !!row?.[`exhaust_done_${r}`] : true;
    if (tDone && eDone) continue;
    if (td && ed) {
      const a = new Date(`${td}T00:00:00`).getTime();
      const b = new Date(`${ed}T00:00:00`).getTime();
      return a <= b ? td : ed;
    }
    return td || ed;
  }
  return '';
}

function diffDaysFromToday(isoDate) {
  if (!isoDate) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(target.getTime())) return null;
  return Math.floor((target - today) / (1000 * 60 * 60 * 24));
}

function getInspectionComputedRemaining(row) {
  return diffDaysFromToday(computeActiveInspectionDueIso(row));
}

function isInspectionOverdue(row, computedRemaining, getRemainingFromRow) {
  if (computedRemaining != null && computedRemaining < 0) return true;
  const raw = getRemainingFromRow?.();
  const n = raw != null && raw !== '' ? Number(raw) : null;
  return Number.isFinite(n) && n < 0;
}

const ROUND_LABELS = ['1. Muayene', '2. Muayene', '3. Muayene', '4. Muayene'];

const InternalAuditInspectionsPage = () => {
  const [rows, setRows] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [formData, setFormData] = useState(initialForm);
  const [openRound, setOpenRound] = useState('round1');
  const [filters, setFilters] = useState({ vehicle_id: getVehicleIdFromSearch() });
  const [draftFilters, setDraftFilters] = useState({ vehicle_id: getVehicleIdFromSearch() });
  const [showVehicleDropdown, setShowVehicleDropdown] = useState(false);
  const vehicleDropdownRef = useRef(null);

  const sortedVehicles = useMemo(() => sortVehiclesByVehicleCode(vehicles), [vehicles]);

  const sortedRows = useMemo(() => sortRowsByVehicleCode(rows), [rows]);

  const selectedVehicle = useMemo(
    () => vehicles.find((v) => String(v.id) === String(formData.vehicle)),
    [vehicles, formData.vehicle],
  );

  const roundDates = useMemo(
    () => computeRounds(formData.tuvturk_date, formData.exhaust_date),
    [formData.tuvturk_date, formData.exhaust_date],
  );

  const loadData = async (activeFilters = filters) => {
    setLoading(true);
    setError(null);
    try {
      const [inspectionsResponse, vehiclesResponse] = await Promise.all([
        vehicleMaintenanceApi.getInspections(activeFilters),
        vehicleMaintenanceApi.getVehicles({ is_active: 'true' }),
      ]);
      setRows(inspectionsResponse?.data || []);
      setVehicles(vehiclesResponse?.data || []);
    } catch (err) {
      setError(err.message || 'Muayene kayıtları yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!vehicleDropdownRef.current) return;
      if (!vehicleDropdownRef.current.contains(event.target)) setShowVehicleDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = () => {
    setFilters(draftFilters);
    setShowVehicleDropdown(false);
    loadData(draftFilters);
  };

  const handleClear = () => {
    const reset = { vehicle_id: '' };
    setDraftFilters(reset);
    setFilters(reset);
    setShowVehicleDropdown(false);
    loadData(reset);
  };

  const openCreateModal = () => {
    setEditingRow(null);
    setSelectedRow(null);
    setFormData(initialForm);
    setOpenRound('round1');
    setIsModalOpen(true);
  };

  const openEditModal = (row) => {
    if (!row) return;
    setEditingRow(row);
    setFormData({
      vehicle: row.vehicle ?? '',
      tuvturk_date: row.tuvturk_date || '',
      exhaust_date: row.exhaust_date || '',
      tuvturk_done_1: !!row.tuvturk_done_1,
      tuvturk_done_2: !!row.tuvturk_done_2,
      tuvturk_done_3: !!row.tuvturk_done_3,
      tuvturk_done_4: !!row.tuvturk_done_4,
      exhaust_done_1: !!row.exhaust_done_1,
      exhaust_done_2: !!row.exhaust_done_2,
      exhaust_done_3: !!row.exhaust_done_3,
      exhaust_done_4: !!row.exhaust_done_4,
      notes: row.notes || '',
    });
    setOpenRound('round1');
    setIsModalOpen(true);
  };

  const handleDelete = async (row) => {
    if (!row) return;
    if (!window.confirm('Muayene kaydını silmek istediğinize emin misiniz?')) return;
    try {
      await vehicleMaintenanceApi.deleteInspection(row.id);
      setSelectedRow(null);
      await loadData();
    } catch (err) {
      setError(err.message || 'Kayıt silinemedi.');
    }
  };

  const buildPayload = () => ({
    vehicle: formData.vehicle ? Number(formData.vehicle) : null,
    tuvturk_date: formData.tuvturk_date || null,
    exhaust_date: formData.exhaust_date || null,
    tuvturk_done_1: formData.tuvturk_done_1,
    tuvturk_done_2: formData.tuvturk_done_2,
    tuvturk_done_3: formData.tuvturk_done_3,
    tuvturk_done_4: formData.tuvturk_done_4,
    exhaust_done_1: formData.exhaust_done_1,
    exhaust_done_2: formData.exhaust_done_2,
    exhaust_done_3: formData.exhaust_done_3,
    exhaust_done_4: formData.exhaust_done_4,
    notes: formData.notes || '',
  });

  const handleSave = async (event) => {
    event.preventDefault();
    if (!formData.tuvturk_date) {
      setError('TUVTURK tarihi zorunludur (1. Muayene).');
      setOpenRound('round1');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = buildPayload();
      if (editingRow?.id) {
        await vehicleMaintenanceApi.updateInspection(editingRow.id, payload);
      } else {
        await vehicleMaintenanceApi.createInspection(payload);
      }
      setIsModalOpen(false);
      setEditingRow(null);
      setSelectedRow(null);
      setFormData(initialForm);
      await loadData();
    } catch (err) {
      setError(err.message || 'Kayıt kaydedilemedi.');
    } finally {
      setSaving(false);
    }
  };

  const toggleDone = (field) => {
    setFormData((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const renderRoundHeader = (roundKey, title, subtitle) => {
    const expanded = openRound === roundKey;
    return (
      <button
        type="button"
        onClick={() => setOpenRound(expanded ? '' : roundKey)}
        className="w-full flex items-start justify-between gap-3 px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900/40 hover:bg-gray-100 dark:hover:bg-gray-900/60 transition-colors"
      >
        <div className="text-left">
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</p>
          <p className="text-xs text-gray-600 dark:text-gray-400">{subtitle}</p>
        </div>
        {expanded ? (
          <ChevronDownIcon className="w-5 h-5 text-gray-500 dark:text-gray-400 mt-0.5 shrink-0" />
        ) : (
          <ChevronRightIcon className="w-5 h-5 text-gray-500 dark:text-gray-400 mt-0.5 shrink-0" />
        )}
      </button>
    );
  };

  const renderRoundFields = (roundIndex) => {
    const r = roundIndex - 1;
    const dates = roundDates[r] || { tuvturk: '', exhaust: '', sonMuayene: '' };
    const editable = roundIndex === 1;
    const tDone = `tuvturk_done_${roundIndex}`;
    const eDone = `exhaust_done_${roundIndex}`;

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-4 pt-2">
        <div className="space-y-1 md:col-span-2 flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[140px] space-y-1">
            <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">1. TÜVTÜRK Tarihi</Label>
            <Input
              type="date"
              readOnly={!editable}
              disabled={!editable}
              className={!editable ? 'bg-gray-100 dark:bg-gray-700/50 cursor-not-allowed' : ''}
              value={editable ? formData.tuvturk_date : dates.tuvturk}
              onChange={
                editable
                  ? (e) => setFormData((prev) => ({ ...prev, tuvturk_date: e.target.value }))
                  : undefined
              }
            />
          </div>
          <label className="flex items-center gap-2 pb-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              checked={formData[tDone]}
              onChange={() => toggleDone(tDone)}
            />
            Muayene yapıldı
          </label>
        </div>
        <div className="space-y-1 md:col-span-2 flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[140px] space-y-1">
            <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Egzoz Tarihi</Label>
            <Input
              type="date"
              readOnly={!editable}
              disabled={!editable}
              className={!editable ? 'bg-gray-100 dark:bg-gray-700/50 cursor-not-allowed' : ''}
              value={editable ? formData.exhaust_date : dates.exhaust}
              onChange={
                editable
                  ? (e) => setFormData((prev) => ({ ...prev, exhaust_date: e.target.value }))
                  : undefined
              }
            />
          </div>
          <label className="flex items-center gap-2 pb-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              checked={formData[eDone]}
              onChange={() => toggleDone(eDone)}
            />
            Muayene yapıldı
          </label>
        </div>
        <div className="space-y-1 md:col-span-2">
          <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Sonraki Muayene Tarihi (2 Yıl Sonrası)</Label>
          <Input
            type="date"
            readOnly
            disabled
            className="bg-gray-100 dark:bg-gray-700/50 cursor-not-allowed"
            value={dates.sonMuayene}
          />
        </div>
      </div>
    );
  };

  const columns = useMemo(
    () => [
      {
        header: 'Araç ID',
        accessorKey: 'vehicle_code',
        cell: ({ getValue }) => (
          <span className="rounded-md bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
            {getValue() || '—'}
          </span>
        ),
      },
      {
        ...vehiclePlateColumnProps,
        cell: ({ row }) => {
          const vehicle = getVehicleById(vehicles, row.original.vehicle);
          return (
            <VehiclePlateCell
              plate={row.original.license_plate}
              brand={vehicle?.brand}
              model={vehicle?.model}
            />
          );
        },
      },
      { header: '1. TÜVTÜRK Tarihi', accessorKey: 'tuvturk_date', cell: ({ getValue }) => getValue() || '—' },
      { header: 'Egzoz Tarihi', accessorKey: 'exhaust_date', cell: ({ getValue }) => getValue() || '—' },
      {
        header: 'Kalan Gün',
        id: 'remaining_days_computed',
        cell: ({ row }) => {
          const computed = getInspectionComputedRemaining(row.original);
          if (isInspectionOverdue(row.original, computed, () => row.original?.remaining_days)) return '—';
          if (computed != null) return computed;
          const apiRem = row.original?.remaining_days;
          return apiRem ?? '—';
        },
      },
      { header: 'Sonraki Muayene (2. Tur)', accessorKey: 'valid_until', cell: ({ getValue }) => getValue() || '—' },
      {
        header: 'Takip Durumu',
        accessorKey: 'follow_up_status',
        cell: ({ row, getValue }) => {
          const computed = getInspectionComputedRemaining(row.original);
          if (isInspectionOverdue(row.original, computed, () => row.original?.remaining_days)) {
            return <StatusBadge status="expired" label="Muayene geçmiştir" />;
          }
          return <StatusBadge status={getValue()} />;
        },
      },
      { header: 'Not', accessorKey: 'notes', cell: ({ getValue }) => getValue() || '—' },
    ],
    [vehicles],
  );

  const stats = useMemo(() => {
    const total = sortedRows.length;
    let active = 0;
    let approaching = 0;
    let expired = 0;
    sortedRows.forEach((row) => {
      const computed = getInspectionComputedRemaining(row);
      if (isInspectionOverdue(row, computed, () => row?.remaining_days) || row.follow_up_status === 'expired') {
        expired += 1;
      } else if (row.follow_up_status === 'approaching') {
        approaching += 1;
      } else if (row.follow_up_status === 'active') {
        active += 1;
      }
    });
    return { total, active, approaching, expired };
  }, [sortedRows]);

  const selectedVehiclePreview = useMemo(() => {
    if (!selectedRow) return null;
    return getVehicleById(vehicles, selectedRow.vehicle) || {
      license_plate: selectedRow.license_plate,
      vehicle_code: selectedRow.vehicle_code,
      brand: null,
      model: null,
      assigned_person: selectedRow.assigned_person,
    };
  }, [selectedRow, vehicles]);

  const getRowClassName = (row) => {
    const computed = getInspectionComputedRemaining(row);
    if (isInspectionOverdue(row, computed, () => row?.remaining_days) || row.follow_up_status === 'expired') {
      return 'border-l-[3px] border-l-rose-400 dark:border-l-rose-500';
    }
    if (row.follow_up_status === 'approaching') {
      return 'border-l-[3px] border-l-amber-400 dark:border-l-amber-500';
    }
    return '';
  };

  return (
    <VehiclePageShell>
        <VehicleModuleHero
          icon={ClipboardDocumentCheckIcon}
          title="TÜVTÜRK - Egzoz Takip"
          subtitle="Araç muayene ve egzoz tarihlerini takip edin."
          accent="teal"
        />

        <VehicleStatGrid>
          <VehicleStatCard label="Toplam Kayıt" value={stats.total} tone="slate" icon={ClipboardDocumentCheckIcon} />
          <VehicleStatCard label="Devam Ediyor" value={stats.active} tone="emerald" icon={CheckCircleIcon} />
          <VehicleStatCard label="Süre Yaklaşıyor" value={stats.approaching} tone="amber" icon={BellAlertIcon} />
          <VehicleStatCard label="Süresi Dolan" value={stats.expired} tone="rose" icon={ExclamationTriangleIcon} />
        </VehicleStatGrid>

        {selectedVehiclePreview ? (
          <VehicleSelectionPreview
            vehicle={selectedVehiclePreview}
            personName={selectedVehiclePreview.assigned_person}
          />
        ) : null}

        {error ? (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
            <div className="flex items-center justify-between gap-3">
              <span>{error}</span>
              <button type="button" onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        ) : null}

        <FilterPanel compact title="Filtreler" isOpen={isFilterOpen} onToggle={() => setIsFilterOpen((prev) => !prev)}>
          <div className="grid grid-cols-1 gap-1.5">
            <div className="rounded-md border border-teal-100/80 bg-gradient-to-br from-white to-teal-50/40 p-1.5 dark:border-teal-900/40 dark:from-gray-800 dark:to-teal-950/20">
              <div className="sr-only text-teal-800 dark:text-teal-300">
                Araç Filtresi
              </div>
              <div className="grid grid-cols-2 gap-1 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-7">
                <fieldset className="rounded border border-gray-200 bg-white px-1 pb-0 pt-0 dark:border-gray-600 dark:bg-gray-700">
                  <legend className="px-0.5 text-[8px] text-gray-500 dark:text-gray-400 leading-none">Araç</legend>
                  <div className="relative" ref={vehicleDropdownRef}>
                    <button
                      type="button"
                      onClick={() => setShowVehicleDropdown((prev) => !prev)}
                      className="flex h-5 w-full items-center justify-between bg-transparent px-0 text-[10px] text-gray-900 outline-none dark:text-white"
                    >
                      <span className="truncate">
                        {(() => {
                          if (!draftFilters.vehicle_id) return 'Tümü';
                          const selected = sortedVehicles.find((v) => String(v.id) === String(draftFilters.vehicle_id));
                          return selected ? `${selected.vehicle_code} - ${selected.license_plate}` : 'Tümü';
                        })()}
                      </span>
                      <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 text-gray-400" />
                    </button>
                    {showVehicleDropdown ? (
                      <div className="absolute left-0 right-0 z-50 mt-1 max-h-[196px] overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg dark:border-gray-600 dark:bg-gray-800">
                        <button
                          type="button"
                          onClick={() => {
                            setDraftFilters((prev) => ({ ...prev, vehicle_id: '' }));
                            setShowVehicleDropdown(false);
                          }}
                          className="w-full px-2 py-1.5 text-left text-[11px] text-gray-900 hover:bg-gray-100 dark:text-white dark:hover:bg-gray-700"
                        >
                          Tümü
                        </button>
                        {sortedVehicles.map((vehicle) => (
                          <button
                            key={vehicle.id}
                            type="button"
                            onClick={() => {
                              setDraftFilters((prev) => ({ ...prev, vehicle_id: String(vehicle.id) }));
                              setShowVehicleDropdown(false);
                            }}
                            className="w-full px-2 py-1.5 text-left text-[11px] text-gray-900 hover:bg-gray-100 dark:text-white dark:hover:bg-gray-700"
                          >
                            {vehicle.vehicle_code} - {vehicle.license_plate}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </fieldset>
              </div>
            </div>
          </div>
          <div className="mt-1.5 flex justify-end border-t border-gray-100 pt-1.5 dark:border-gray-700">
            <FilterActions compact onClear={handleClear} onSearch={handleSearch} />
          </div>
        </FilterPanel>

        <ActionToolbar className="rounded-xl border border-teal-100/80 bg-white/80 px-2 py-1.5 shadow-sm dark:border-teal-900/40 dark:bg-gray-900/60">
          <IconActionButton icon={PlusIcon} onClick={openCreateModal} title="Yeni Kayıt" />
          <IconActionButton icon={PencilSquareIcon} onClick={() => openEditModal(selectedRow)} title="Düzenle" disabled={!selectedRow} />
          <IconActionButton icon={TrashIcon} onClick={() => handleDelete(selectedRow)} title="Sil" disabled={!selectedRow} />
        </ActionToolbar>

        <DataTable
          data={sortedRows}
          columns={columns}
          isLoading={loading}
          showSearch={false}
          onRowClick={setSelectedRow}
          onRowDoubleClick={openEditModal}
          selectedRowId={selectedRow?.id}
          compact
          compactCellWrap
          modern
          getRowClassName={getRowClassName}
        />

      <ResponsiveModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} className="max-w-4xl max-h-[92dvh]">
        <div className="px-4 pt-5 pb-4 sm:p-6 flex flex-col max-h-[88vh]">
          <div className="flex items-center justify-between mb-4 shrink-0">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white">
              {editingRow ? 'Muayene Kaydını Düzenle' : 'Yeni Muayene Kaydı'}
            </h2>
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSave} className="flex flex-col min-h-0 flex-1">
            <div className="overflow-y-auto space-y-4 pr-1 flex-1">
              <div className="space-y-1">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Araç</Label>
                <select
                  required
                  className="flex h-11 md:h-10 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white"
                  value={formData.vehicle}
                  onChange={(e) => setFormData((prev) => ({ ...prev, vehicle: e.target.value }))}
                >
                  <option value="">Araç seçin</option>
                  {sortedVehicles.map((vehicle) => (
                    <option key={vehicle.id} value={vehicle.id}>
                      {vehicle.vehicle_code} - {vehicle.license_plate}
                    </option>
                  ))}
                </select>
              </div>

              {formData.vehicle ? (
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Tescil Tarihi (araç kaydı)</Label>
                  <Input
                    type="date"
                    readOnly
                    disabled
                    className="bg-gray-100 dark:bg-gray-700/50 cursor-not-allowed"
                    value={selectedVehicle?.registration_date || ''}
                  />
                </div>
              ) : null}

              {[1, 2, 3, 4].map((num) => (
                <div key={num} className="space-y-2">
                  {renderRoundHeader(
                    `round${num}`,
                    ROUND_LABELS[num - 1],
                    num === 1 ? '1. muayene tarihlerini girin (sıfır araçta tescilden 3 yıl sonra)' : 'Sonraki turlar, 1. muayeneden itibaren 2 yılda bir otomatik hesaplanır',
                  )}
                  {openRound === `round${num}` ? renderRoundFields(num) : null}
                </div>
              ))}

              <div className="space-y-1">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Notlar</Label>
                <Input
                  type="text"
                  value={formData.notes}
                  onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
                />
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end space-x-3 shrink-0 pt-2 border-t border-gray-200 dark:border-gray-700">
              <Button variant="outline" type="button" onClick={() => setIsModalOpen(false)}>
                İptal
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white" disabled={saving}>
                {saving ? 'Kaydediliyor...' : 'Kaydet'}
              </Button>
            </div>
          </form>
        </div>
      </ResponsiveModal>
    </VehiclePageShell>
  );
};

export default InternalAuditInspectionsPage;
